import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createTranslator, LOCALES, pickText } from "@heylife/i18n";
import { api, type Localized } from "../api";
import { useT } from "../i18n";
import { ConfirmDelete, Field, GripIcon, LocalizedField, Msg, splitLines, Toggle, useFlash } from "../ui";
import { PathForm, type PathRow } from "./Content";

function parseVideoId(input: string): string | null {
  const raw = input.trim();
  if (/^[\w-]{11}$/.test(raw)) return raw;
  const match = raw.match(/(?:v=|youtu\.be\/|embed\/|shorts\/|live\/)([\w-]{11})/);
  return match?.[1] ?? null;
}

const defaultCopy = {
  reflection: {
    de: createTranslator("de").t("admin.content.defaultReflection"),
    en: createTranslator("en").t("admin.content.defaultReflection"),
  },
  step: {
    de: createTranslator("de").t("admin.content.defaultStepCard"),
    en: createTranslator("en").t("admin.content.defaultStepCard"),
  },
};

interface QuestionRow {
  id: string;
  sessionId: string;
  position: number;
  kind: "multiple_choice" | "yes_no" | "free";
  prompt: Localized;
  options: Record<string, string[]>;
  correctIndex: number;
  explanation: Localized;
}

interface SessionRow {
  id: string;
  pathId: string;
  position: number;
  title: Localized;
  videoId: string;
  videoDurationSec: number | null;
  captions: boolean;
  reflectionPrompt: Localized;
  stepCard: Localized | null;
  questions: QuestionRow[];
}

export function PathEditor({ id }: { id: string }) {
  const { t, locale } = useT();
  const qc = useQueryClient();
  const key = ["admin", "content", "path", id];
  const q = useQuery({ queryKey: key, queryFn: () => api<{ path: PathRow; sessions: SessionRow[] }>(`/admin/content/paths/${id}`) });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ["admin", "content", "paths"] });
  };
  const [editMeta, setEditMeta] = useState(false);
  const [newSession, setNewSession] = useState(false);
  const delSession = useMutation({ mutationFn: (sid: string) => api(`/admin/content/sessions/${sid}`, { method: "DELETE" }), onSuccess: invalidate });

  const reorderSessions = useMutation({
    mutationFn: (ids: string[]) => api(`/admin/content/paths/${id}/sessions/reorder`, { method: "POST", json: { ids } }),
    onSuccess: invalidate,
  });

  const togglePublish = useMutation({
    mutationFn: (published: boolean) => api(`/admin/content/paths/${id}`, { method: "PATCH", json: { published } }),
    onSuccess: invalidate,
  });

  const [sessionList, setSessionList] = useState<SessionRow[]>([]);
  const [draggedSessionId, setDraggedSessionId] = useState<string | null>(null);
  const [dragOverSessionId, setDragOverSessionId] = useState<string | null>(null);

  useEffect(() => {
    if (q.data?.sessions) {
      setSessionList(q.data.sessions);
    }
  }, [q.data?.sessions]);

  if (q.isLoading) return <p className="muted">{t("admin.common.loading")}</p>;
  if (!q.data) return <p className="error">{t("admin.common.error")}</p>;
  const { path } = q.data;

  const handleSessionDrop = (targetId: string) => {
    if (!draggedSessionId || draggedSessionId === targetId) return;
    const currentIds = sessionList.map((s) => s.id);
    const from = currentIds.indexOf(draggedSessionId);
    const to = currentIds.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const next = [...sessionList];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    setSessionList(next);
    setDraggedSessionId(null);
    setDragOverSessionId(null);
    reorderSessions.mutate(next.map((s) => s.id));
  };

  return (
    <>
      <Link to="/admin/content" search={{ tab: "paths" }} className="muted backlink">
        ← {t("admin.content.tabs.paths")}
      </Link>
      <div className="toolbar">
        <div>
          <p className="eyebrow">{path.slug}</p>
          <h1>{pickText(path.title, locale)}</h1>
          <p className="muted">{pickText(path.description, locale)}</p>
        </div>
        <div className="actions">
          <span className={`pill ${path.published ? "live" : "warn"}`}>
            {path.published ? t("admin.content.pathVisible") : t("admin.content.pathHidden")}
          </span>
          <button
            type="button"
            className={`btn small ${path.published ? "secondary" : "live-btn"}`}
            disabled={togglePublish.isPending}
            onClick={() => togglePublish.mutate(!path.published)}
            title={path.published ? t("admin.content.pathVisibleHint") : t("admin.content.pathHiddenHint")}
          >
            {path.published ? t("admin.content.hidePath") : t("admin.content.showPath")}
          </button>
          <button className="btn small ghost" onClick={() => setEditMeta((v) => !v)}>
            {t("admin.common.edit")}
          </button>
        </div>
      </div>
      {!path.published ? (
        <div className="path-hidden-banner">
          <span>⚠️</span>
          <div>
            <strong>{t("admin.content.pathHidden")}</strong> — {t("admin.content.pathHiddenHint")}
          </div>
        </div>
      ) : null}
      {editMeta ? <PathForm path={path} onClose={() => setEditMeta(false)} onSaved={invalidate} /> : null}

      <h2 className="section">{t("admin.content.tabs.paths")} · {t("admin.content.sessions", { n: sessionList.length })}</h2>
      <div className="stack">
        {sessionList.map((s) => (
          <SessionCard
            key={s.id}
            session={s}
            isDragging={draggedSessionId === s.id}
            isDragOver={dragOverSessionId === s.id}
            onDragStart={(e) => {
              e.dataTransfer.setData("text/plain", s.id);
              e.dataTransfer.effectAllowed = "move";
              setDraggedSessionId(s.id);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              if (draggedSessionId && draggedSessionId !== s.id) {
                setDragOverSessionId(s.id);
              }
            }}
            onDragEnd={() => {
              setDraggedSessionId(null);
              setDragOverSessionId(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              handleSessionDrop(s.id);
            }}
            onChanged={invalidate}
            onDelete={() => delSession.mutate(s.id)}
          />
        ))}
      </div>
      {newSession ? (
        <SessionForm pathId={path.id} position={sessionList.length + 1} onClose={() => setNewSession(false)} onSaved={invalidate} />
      ) : (
        <button className="btn small" style={{ marginTop: 16 }} onClick={() => setNewSession(true)}>
          {t("admin.content.newSession")}
        </button>
      )}
    </>
  );
}

function fmtDurationSec(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

function SessionCard({
  session: s,
  isDragging,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  onChanged,
  onDelete,
}: {
  session: SessionRow;
  isDragging?: boolean;
  isDragOver?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onChanged: () => void;
  onDelete: () => void;
}) {
  const { t, locale } = useT();
  const [edit, setEdit] = useState(false);
  const [newQ, setNewQ] = useState(false);
  const delQ = useMutation({ mutationFn: (qid: string) => api(`/admin/content/questions/${qid}`, { method: "DELETE" }), onSuccess: onChanged });

  const reorderQuestions = useMutation({
    mutationFn: (ids: string[]) => api(`/admin/content/sessions/${s.id}/questions/reorder`, { method: "POST", json: { ids } }),
    onSuccess: onChanged,
  });

  const [questionList, setQuestionList] = useState<QuestionRow[]>([]);
  const [draggedQId, setDraggedQId] = useState<string | null>(null);
  const [dragOverQId, setDragOverQId] = useState<string | null>(null);

  useEffect(() => {
    setQuestionList(s.questions);
  }, [s.questions]);

  const handleQuestionDrop = (targetId: string) => {
    if (!draggedQId || draggedQId === targetId) return;
    const currentIds = questionList.map((q) => q.id);
    const from = currentIds.indexOf(draggedQId);
    const to = currentIds.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const next = [...questionList];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    setQuestionList(next);
    setDraggedQId(null);
    setDragOverQId(null);
    reorderQuestions.mutate(next.map((q) => q.id));
  };

  return (
    <div
      className={`card session draggable-item ${isDragging ? "is-dragging" : ""} ${isDragOver ? "drag-over-top" : ""}`}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="listrow">
        <span className="drag-handle" title={t("admin.common.dragToReorder")} draggable onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <GripIcon />
        </span>
        <span className="num">{s.position}</span>
        <img src={`https://i.ytimg.com/vi/${s.videoId}/mqdefault.jpg`} alt="" width={96} height={54} />
        <div className="grow">
          <strong>{pickText(s.title, locale)}</strong>
          <div className="muted">
            {s.videoId} · {s.videoDurationSec ? `${fmtDurationSec(s.videoDurationSec)} (${s.videoDurationSec}s)` : ""} {s.captions ? "· CC" : ""} · {t("admin.content.questions")}: {s.questions.length}
          </div>
        </div>
        <button className="btn small ghost" onClick={() => setEdit((v) => !v)}>
          {t("admin.common.edit")}
        </button>
        <ConfirmDelete onConfirm={onDelete} />
      </div>
      {edit ? <SessionForm pathId={s.pathId} session={s} position={s.position} onClose={() => setEdit(false)} onSaved={onChanged} /> : null}

      <div className="questions">
        {questionList.map((qu) => (
          <QuestionRowView
            key={qu.id}
            q={qu}
            isDragging={draggedQId === qu.id}
            isDragOver={dragOverQId === qu.id}
            onDragStart={(e) => {
              e.stopPropagation();
              e.dataTransfer.setData("text/plain", qu.id);
              e.dataTransfer.effectAllowed = "move";
              setDraggedQId(qu.id);
            }}
            onDragOver={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              if (draggedQId && draggedQId !== qu.id) {
                setDragOverQId(qu.id);
              }
            }}
            onDragEnd={(e) => {
              e.stopPropagation();
              setDraggedQId(null);
              setDragOverQId(null);
            }}
            onDrop={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleQuestionDrop(qu.id);
            }}
            onChanged={onChanged}
            onDelete={() => delQ.mutate(qu.id)}
          />
        ))}
        {newQ ? (
          <QuestionForm sessionId={s.id} position={s.questions.length + 1} onClose={() => setNewQ(false)} onSaved={onChanged} />
        ) : (
          <button className="btn small ghost" onClick={() => setNewQ(true)}>
            + {t("admin.content.newQuestion")}
          </button>
        )}
      </div>
    </div>
  );
}

function SessionForm({ pathId, session, position, onClose, onSaved }: { pathId: string; session?: SessionRow; position: number; onClose: () => void; onSaved: () => void }) {
  const { t, locale } = useT();
  const [f, setF] = useState<Partial<SessionRow>>(
    session ?? {
      position,
      title: {},
      videoId: "",
      videoDurationSec: null,
      captions: true,
      reflectionPrompt: {},
      stepCard: {},
    }
  );
  const [urlInput, setUrlInput] = useState(session?.videoId ?? "");
  const [fetchingYt, setFetchingYt] = useState(false);
  const fetchSeq = useRef(0);
  const [flash, run, busy] = useFlash();

  const handleUrlChange = (val: string) => {
    setUrlInput(val);
    setF((prev) => ({ ...prev, videoId: parseVideoId(val) ?? "" }));
  };

  useEffect(() => {
    const raw = urlInput.trim();
    const unchanged = Boolean(session?.videoId && (raw === session.videoId || raw === `https://www.youtube.com/watch?v=${session.videoId}`));
    if (!raw || unchanged || !parseVideoId(raw)) {
      setFetchingYt(false);
      return;
    }
    const seq = ++fetchSeq.current;
    const timer = setTimeout(() => {
      void (async () => {
        setFetchingYt(true);
        try {
          const res = await api<{ metadata: { videoId: string; title: string; durationSec: number | null; thumbnail: string } }>(
            `/admin/content/youtube-metadata?url=${encodeURIComponent(raw)}`,
          );
          if (seq !== fetchSeq.current || !res?.metadata) return;
          const { videoId, title, durationSec } = res.metadata;
          setF((prev) => ({
            ...prev,
            videoId,
            videoDurationSec: durationSec ?? prev.videoDurationSec,
            title: {
              de: prev.title?.de || title,
              en: prev.title?.en || title,
            },
            reflectionPrompt: prev.reflectionPrompt && Object.values(prev.reflectionPrompt).some(Boolean) ? prev.reflectionPrompt : defaultCopy.reflection,
            stepCard: prev.stepCard && Object.values(prev.stepCard).some(Boolean) ? prev.stepCard : defaultCopy.step,
          }));
        } catch (err) {
          if (seq === fetchSeq.current) console.warn("Failed to fetch YouTube metadata:", err);
        } finally {
          if (seq === fetchSeq.current) setFetchingYt(false);
        }
      })();
    }, 500);
    return () => {
      clearTimeout(timer);
      fetchSeq.current += 1;
    };
  }, [urlInput, session?.videoId]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanId = parseVideoId(urlInput || f.videoId || "");

    const body = {
      position: Number(f.position),
      title: f.title,
      videoId: cleanId ?? "",
      videoDurationSec: f.videoDurationSec ? Number(f.videoDurationSec) : null,
      captions: Boolean(f.captions),
      reflectionPrompt: f.reflectionPrompt,
      stepCard: f.stepCard && Object.values(f.stepCard).some(Boolean) ? f.stepCard : null,
    };
    if (
      await run(async () => {
        if (!cleanId) throw new Error(t("admin.content.invalidYoutube"));
        return session
          ? api(`/admin/content/sessions/${session.id}`, { method: "PATCH", json: { ...body, videoId: cleanId } })
          : api(`/admin/content/paths/${pathId}/sessions`, { method: "POST", json: { ...body, videoId: cleanId } });
      })
    ) {
      onSaved();
      onClose();
    }
  };

  return (
    <form className="form inner" onSubmit={save}>
      <div className="two-col">
        <Field label={t("admin.common.position")}>
          <input type="number" min={1} max={99} value={f.position ?? 1} onChange={(e) => setF({ ...f, position: Number(e.target.value) })} />
        </Field>
        <Field label={t("admin.content.videoId")}>
          <input
            required
            placeholder="https://www.youtube.com/watch?v=... or Video ID"
            value={urlInput}
            onChange={(e) => handleUrlChange(e.target.value)}
          />
          {fetchingYt ? (
            <div style={{ marginTop: 4, fontSize: 12, color: "var(--ink-muted)" }}>
              {t("admin.content.youtubeFetching")}
            </div>
          ) : null}
        </Field>
        <Field label={t("admin.content.videoDuration")}>
          <input
            type="number"
            min={1}
            max={14400}
            value={f.videoDurationSec ?? ""}
            placeholder={f.videoDurationSec ? String(f.videoDurationSec) : "e.g. 120"}
            onChange={(e) => setF({ ...f, videoDurationSec: e.target.value ? Number(e.target.value) : null })}
          />
        </Field>
      </div>

      {/* Whole Card Preview */}
      {parseVideoId(f.videoId ?? "") ? (
        <div className="session-preview-card">
          <div className="eyebrow">{t("admin.content.previewCard")}</div>
          <div className="listrow preview-listrow">
            <span className="num">{f.position ?? position}</span>
            <img
              src={`https://i.ytimg.com/vi/${f.videoId}/mqdefault.jpg`}
              alt=""
              width={96}
              height={54}
              style={{ borderRadius: 8, objectFit: "cover" }}
            />
            <div className="grow">
              <strong>{pickText(f.title as Localized, locale) || t("admin.content.sessionTitle")}</strong>
              <div className="muted" style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
                <span>{f.videoId}</span>
                {f.videoDurationSec ? <span>· {fmtDurationSec(f.videoDurationSec)} ({f.videoDurationSec}s)</span> : null}
                {f.captions ? <span>· CC</span> : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <LocalizedField label={t("admin.content.sessionTitle")} required value={f.title} onChange={(title) => setF({ ...f, title })} />
      <LocalizedField label={t("admin.content.reflectionPrompt")} required multiline value={f.reflectionPrompt} onChange={(reflectionPrompt) => setF({ ...f, reflectionPrompt })} />
      <LocalizedField label={t("admin.content.stepCard")} multiline value={f.stepCard} onChange={(stepCard) => setF({ ...f, stepCard })} />
      <Toggle label={t("admin.content.captions")} checked={Boolean(f.captions)} onChange={(captions) => setF({ ...f, captions })} />
      <div className="actions">
        <button className="btn small" disabled={busy || fetchingYt}>
          {t("admin.common.save")}
        </button>
        <button type="button" className="btn small ghost" onClick={onClose}>
          {t("admin.common.cancel")}
        </button>
      </div>
      <Msg msg={flash} />
    </form>
  );
}

function QuestionRowView({
  q,
  isDragging,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  onChanged,
  onDelete,
}: {
  q: QuestionRow;
  isDragging?: boolean;
  isDragOver?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onChanged: () => void;
  onDelete: () => void;
}) {
  const { t, locale } = useT();
  const [edit, setEdit] = useState(false);
  const opts = q.options[locale] ?? q.options.de ?? [];
  return (
    <div
      className={`question draggable-item ${isDragging ? "is-dragging" : ""} ${isDragOver ? "drag-over-top" : ""}`}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="listrow">
        <span className="drag-handle" title={t("admin.common.dragToReorder")} draggable onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <GripIcon />
        </span>
        <span className="num small">{q.position}</span>
        <div className="grow">
          <div>
            <strong>{pickText(q.prompt, locale)}</strong>
            <span className={`pill small ${q.kind === "free" ? "live" : ""}`} style={{ marginLeft: 8 }}>
              {t(`admin.content.${q.kind === "free" ? "free" : q.kind === "yes_no" ? "yesNo" : "multipleChoice"}`)}
            </span>
          </div>
          <ol className="opts">
            {opts.map((o, i) => (
              <li key={i} className={q.kind !== "free" && i === q.correctIndex ? "correct" : ""}>
                {o}
              </li>
            ))}
          </ol>
        </div>
        <button className="btn small ghost" onClick={() => setEdit((v) => !v)}>
          {t("admin.common.edit")}
        </button>
        <ConfirmDelete onConfirm={onDelete} />
      </div>
      {edit ? <QuestionForm sessionId={q.sessionId} question={q} position={q.position} onClose={() => setEdit(false)} onSaved={onChanged} /> : null}
    </div>
  );
}

function QuestionForm({ sessionId, question, position, onClose, onSaved }: { sessionId: string; question?: QuestionRow; position: number; onClose: () => void; onSaved: () => void }) {
  const { t } = useT();
  const [f, setF] = useState<{ position: number; kind: "multiple_choice" | "yes_no" | "free"; prompt: Localized; options: Record<string, string>; correctIndex: number; explanation: Localized }>({
    position: question?.position ?? position,
    kind: question?.kind ?? "multiple_choice",
    prompt: question?.prompt ?? {},
    options: Object.fromEntries(LOCALES.map((l) => [l, (question?.options[l] ?? []).join("\n")])),
    correctIndex: question?.correctIndex ?? 0,
    explanation: question?.explanation ?? {},
  });
  const [flash, run, busy] = useFlash();
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = Object.fromEntries(LOCALES.map((l) => [l, splitLines(f.options[l] ?? "")]));
    const body = {
      position: Number(f.position),
      kind: f.kind,
      prompt: f.prompt,
      options: parsed,
      correctIndex: f.kind === "free" ? 0 : Number(f.correctIndex),
      explanation: f.explanation,
    };
    if (
      await run(async () => {
        if (f.kind !== "free") {
          const lists = Object.values(parsed);
          if (lists.every((lines) => lines.length < 2) || lists.some((lines) => lines.length > 0 && lines.length < 2)) {
            throw new Error(t("admin.content.optionsNeedTwo"));
          }
          const shortest = Math.min(...lists.filter((lines) => lines.length >= 2).map((lines) => lines.length));
          if (body.correctIndex >= shortest) throw new Error(t("admin.content.correctIndexRange"));
        }
        if (!f.explanation.de?.trim()) throw new Error(t("admin.errors.required", { field: t("admin.errors.fields.explanation_de") }));
        if (!f.explanation.en?.trim()) throw new Error(t("admin.errors.required", { field: t("admin.errors.fields.explanation_en") }));
        return question
          ? api(`/admin/content/questions/${question.id}`, { method: "PATCH", json: body })
          : api(`/admin/content/sessions/${sessionId}/questions`, { method: "POST", json: body });
      })
    ) {
      onSaved();
      onClose();
    }
  };
  return (
    <form className="form inner" onSubmit={save}>
      <div className="two-col">
        <Field label={t("admin.common.position")}>
          <input type="number" min={1} max={20} value={f.position} onChange={(e) => setF({ ...f, position: Number(e.target.value) })} />
        </Field>
        <Field label={t("admin.content.kind")}>
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as "multiple_choice" | "yes_no" | "free" })}>
            <option value="multiple_choice">{t("admin.content.multipleChoice")}</option>
            <option value="yes_no">{t("admin.content.yesNo")}</option>
            <option value="free">{t("admin.content.free")}</option>
          </select>
        </Field>
        <Field label={t("admin.content.correctIndex")}>
          <input
            type="number"
            min={1}
            max={6}
            disabled={f.kind === "free"}
            value={f.kind === "free" ? "" : f.correctIndex + 1}
            placeholder={f.kind === "free" ? t("admin.content.noCorrectAnswer") : undefined}
            onChange={(e) => setF({ ...f, correctIndex: Math.max(0, Number(e.target.value) - 1) })}
          />
        </Field>
      </div>
      <LocalizedField label={t("admin.content.prompt")} required value={f.prompt} onChange={(prompt) => setF({ ...f, prompt })} />
      <div className="field">
        <span className="lbl">
          {t("admin.content.options")}
          {f.kind !== "free" ? (
            <abbr className="req" title={t("admin.common.required")}>
              *
            </abbr>
          ) : null}
        </span>
        <span className="muted hint">{f.kind === "free" ? t("admin.content.optionsOptional") : t("admin.content.optionsNeedTwo")}</span>
        <div className="pair">
          {LOCALES.map((l) => (
            <textarea key={l} rows={4} placeholder={t(`admin.common.${l}`)} required={f.kind !== "free" && l === "de"} value={f.options[l] ?? ""} onChange={(e) => setF({ ...f, options: { ...f.options, [l]: e.target.value } })} />
          ))}
        </div>
      </div>
      <LocalizedField label={t("admin.content.explanation")} required multiline value={f.explanation} onChange={(explanation) => setF({ ...f, explanation })} />
      <div className="actions">
        <button className="btn small" disabled={busy}>
          {t("admin.common.save")}
        </button>
        <button type="button" className="btn small ghost" onClick={onClose}>
          {t("admin.common.cancel")}
        </button>
      </div>
      <Msg msg={flash} />
    </form>
  );
}
